import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, BadgeCheck, ShieldCheck, Sparkles, Trophy, Truck, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Seo } from "@/lib/seo";

const SPECIAL_FILTERS = [
  { k: "verified", label: "JohnnyMart Verified", Icon: ShieldCheck },
  { k: "new_arrivals", label: "New Arrivals", Icon: Sparkles },
  { k: "best_sellers", label: "Best Sellers", Icon: Trophy },
  { k: "free_shipping", label: "Free Shipping", Icon: Truck },
  { k: "clearance", label: "Clearance", Icon: Tag },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "All Items";
  const search = params.get("search") || "";
  const special = params.get("special") || "";
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(["All Items", ...r.data])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = { collection: "personal" };
    if (category && category !== "All Items") q.category = category;
    if (search) q.search = search;
    if (special) q.special = special;
    api
      .get("/products", { params: q })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, search, special]);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All Items") next.delete("category");
    else next.set("category", c);
    setParams(next);
  };

  const setSpecial = (s) => {
    const next = new URLSearchParams(params);
    if (!s || special === s) next.delete("special");
    else next.set("special", s);
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (searchInput) next.set("search", searchInput);
    else next.delete("search");
    setParams(next);
  };

  const clearSearch = () => {
    setSearchInput("");
    const next = new URLSearchParams(params);
    next.delete("search");
    setParams(next);
  };

  return (
    <div data-testid="shop-page" className="jm-container py-12">
      <Seo title="Shop All Treasures" path="/shop" description="Browse JohnnyMart's hand-picked resale treasures — clothing, handbags, electronics, collectibles, toys and home goods. New finds added often, shipped from Ponte Vedra Beach, FL." />
      <p className="uppercase tracking-[0.2em] text-brick font-bold text-sm">The Shop</p>
      <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">All Treasures</h1>

      <form onSubmit={submitSearch} className="mt-6 flex flex-col sm:flex-row sm:items-center gap-2 max-w-xl">
        <div className="flex items-center bg-white rounded-md border border-navy/20 px-4 h-12 flex-1 min-w-0">
          <Search className="w-5 h-5 text-navy/50 shrink-0" />
          <input
            data-testid="shop-search-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, category, or keyword..."
            className="flex-1 min-w-0 bg-transparent outline-none px-3"
          />
          {searchInput && (
            <button type="button" data-testid="shop-search-clear" onClick={clearSearch} className="text-navy/40 hover:text-brick shrink-0">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button data-testid="shop-search-btn" className="bg-brick text-white font-bold uppercase text-sm rounded-md px-5 h-11 sm:h-12 hover:bg-navy transition-colors shrink-0">
          Search
        </button>
      </form>

      {search && (
        <p className="mt-3 text-sm text-ink/60" data-testid="shop-search-active">
          Showing results for "<b className="text-navy">{search}</b>" ·{" "}
          <button onClick={clearSearch} className="text-brick font-bold underline">clear</button>
        </p>
      )}

      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-navy/60 text-sm font-bold uppercase mr-1">
          <BadgeCheck className="w-4 h-4" /> Collections
        </span>
        {SPECIAL_FILTERS.map((f) => (
          <button
            key={f.k}
            data-testid={`special-filter-${f.k}`}
            onClick={() => setSpecial(f.k)}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${
              special === f.k ? "bg-teal-700 text-white" : "bg-white border border-navy/20 text-navy hover:border-teal-700"
            }`}
          >
            {f.Icon && <f.Icon className="w-3.5 h-3.5" />} {f.label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-navy/60 text-sm font-bold uppercase mr-1">
          <SlidersHorizontal className="w-4 h-4" /> Filter
        </span>
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c}`}
            onClick={() => setCategory(c)}
            className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
              category === c ? "bg-brick text-white" : "bg-white border border-navy/20 text-navy hover:border-brick"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-12 text-center text-navy/50">Loading treasures...</p>
      ) : products.length === 0 ? (
        <div data-testid="shop-empty" className="mt-16 text-center">
          <p className="font-display text-2xl text-navy">No items found</p>
          <p className="text-ink/60 mt-2">Try a different category or search term.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}
